/* eslint-disable react/no-unused-prop-types */
import React, { FC, useState, useEffect, useMemo } from 'react'
import { Drawer, Collapse } from '@mui/material'
import {
  IoCloseOutline,
  IoChevronDownSharp,
  IoChevronUpSharp,
} from 'react-icons/io5'
import router from 'next/router'
import { getColorBackground } from '../../utils/product'
import { Filter } from '../../../types/storefront'
import { ProductFilterKeys } from '../../../types/product'
import money from '../../utils/money'
import {
  PRICE_FILTER_SET,
  COLOR_FILTERS_SET,
  SIZE_FILTER_SET,
  FilterObject,
  PRODUCT_SET,
} from '../../utils/constants/filters'
import ProductTypeItem from './ProductTypeItem'
import FilterItem from './FilterItem'
import { FilterEventHandler, ProductTypeFilter } from './types'

interface Props {
  open?: boolean
  defaultSelect?: FilterObject[]
  filters?: Record<string, Filter>
  onSelect?: FilterEventHandler
  onChange?: FilterEventHandler
  onRemove?: FilterEventHandler
  onOpenChange?: (open: boolean) => any
}

const FILTER_TYPE = ['product_type', 'color', 'size', 'price']

const FilterPanel: FC<Props> = (props) => {
  const { filters } = props
  const {
    open = false,
    onOpenChange,
    onSelect,
    defaultSelect,
    onChange,
  } = props
  const [visible, setVisible] = useState(open)
  const [selected, select] = useState<FilterObject[]>([])
  const [openMap, setOpenMap] = useState(
    FILTER_TYPE.reduce<Record<string, boolean>>((acc, i) => {
      if (['product_type', 'color'].includes(i)) {
        acc[i] = true
      } else {
        acc[i] = false
      }
      return acc
    }, {})
  )

  const formatPriceTag = (item: FilterObject) => {
    if (!item.params) return ''

    const { min, max } = item.params.price
    if (typeof max === 'number' && typeof min === 'number') {
      return `${money(min).formatPrice(2)}-${money(max).formatPrice(2)}`
    }
    return `Over ${money(min || max || 0).formatPrice(2)}`
  }

  // Format filter item to selected tag
  const formatTag = (type: string, item: FilterObject) => {
    if (type === 'price') {
      return formatPriceTag(item)
    }
    return item.label || ''
  }

  const checkSelected = (type: string, label: string) =>
    selected.find((i) => i.label.toLowerCase() === label.toLowerCase())
  const checkPriceSelected = (type: string) => {
    if (type === ProductFilterKeys.price)
      return selected.find((i) => i.type === ProductFilterKeys.price)
    return undefined
  }

  const handleSelect = (items: FilterObject[], isRemove = false) => {
    const data = isRemove ? items : [...selected, ...items]
    setVisible(false)
    select(data)
    onSelect?.(data)
  }
  const handleRemove = (value: FilterObject[]) => {
    const valMap = value.reduce<Record<string, FilterObject>>((acc, i) => {
      acc[i.label.toLowerCase()] = i
      return acc
    }, {})
    const temp = [...selected].filter((i) => !valMap[i.label.toLowerCase()])

    return temp
  }

  /**
   * Select multiple product type
   */
  const selectMultipleItem = (
    type: ProductFilterKeys,
    values: FilterObject[],
    isRemove = false
  ) => {
    let s = values
    if (isRemove) {
      s = handleRemove(values)
    }
    handleSelect(s, isRemove)
  }

  const selectItem = (type: ProductFilterKeys, value: FilterObject) => {
    const isSelected = checkSelected(type, value.label)
    const isPriceSelect = checkPriceSelected(type)
    let s = [value]
    if (isSelected) {
      s = handleRemove([isSelected])
      handleSelect(s, true)
      return
    }
    if (isPriceSelect) {
      s = handleRemove([isPriceSelect])
      handleSelect([...s, value], true)
      return
    }

    handleSelect([value])
  }

  const getTitle = (type: string) => {
    if (type === 'productType') return 'category'
    if (type === 'price') return 'price range'

    return type
  }

  /**
   * Product type filter set
   */
  const productFilterSet = useMemo<ProductTypeFilter[]>(() => {
    const map: any = {}
    filters?.product_type.values.forEach((p) => {
      PRODUCT_SET.forEach((i) => {
        if (!map[i.name] && i.name.toLowerCase() === p.label.toLowerCase()) {
          map[i.name] = { label: i.name, type: ProductFilterKeys.type }
        }
        if (
          i.childrenStr &&
          i.childrenStr
            .toLocaleLowerCase()
            .split('+')
            .includes(p.label.toLowerCase())
        ) {
          if (!map[i.name]) {
            map[i.name] = { label: i.name, type: ProductFilterKeys.type }
          }
          const item = map[i.name]
          if (!item.children) {
            item.children = []
          }
          item.children.push(p)
        }
      })
    })

    return Object.keys(map).map((i) => map[i])
  }, [filters])

  const getCollapseValue = (type: string) => {
    if (type === 'price') {
      let max = 40
      try {
        max = JSON.parse(filters?.price.values[0].input || {})?.price.max
      } catch (e) {
        console.log(e)
      }

      return PRICE_FILTER_SET.filter((i) => (i.params?.price?.min || 0) < max)
    }
    if (type === 'color') {
      return COLOR_FILTERS_SET.filter((i) =>
        filters?.color.values.some(
          (v: any) => i.input.toLowerCase().indexOf(v.label.toLowerCase()) >= 0
        )
      )
    }
    if (type === 'product_type') {
      return productFilterSet
    }
    if (type === ProductFilterKeys.size) {
      return SIZE_FILTER_SET.filter((i) =>
        filters?.size.values.some((v) => v.label.indexOf(i.label) >= 0)
      )
    }
    return filters?.[type]?.values
  }

  useEffect(() => {
    onChange?.(selected)
  }, [selected])
  useEffect(() => {
    if (defaultSelect) {
      select(defaultSelect)
    }
  }, [router.router?.asPath])

  useEffect(() => {
    if (open !== visible) {
      setVisible(open)
    }
  }, [open])

  useEffect(() => {
    onOpenChange?.(visible)
  }, [visible])

  return (
    <Drawer
      className="select-none"
      open={visible}
      onClose={() => setVisible(false)}
    >
      <div className="w-76 px-3">
        <div className="border-b border-gray-200 py-4 flex justify-between">
          <span className="font-semibold">FILTER</span>
          <div className="cursor-pointer" onClick={() => setVisible(false)}>
            <IoCloseOutline size={24} />
          </div>
        </div>

        {selected.length ? (
          <div className="mb-8">
            {selected.map((filter) => (
              // Object.keys(filter).map((key) => {
              // const item = (filter as any)[key]
              <div
                className="py-3 pl-4 bg-black text-white flex items-center justify-between font-semibold tracking-wider text-tiny mb-3"
                key={filter.label}
              >
                <span>{formatTag(filter.type, filter).toUpperCase()}</span>
                <span
                  className="inline-block px-3 text-lg cursor-pointer"
                  onClick={() => handleSelect(handleRemove([filter]), true)}
                >
                  <IoCloseOutline />
                </span>
              </div>
            ))}
          </div>
        ) : null}

        <div className="py-4">
          {(FILTER_TYPE as ProductFilterKeys[]).map((type) => (
            <div className="mb-2" key={type}>
              <div
                className="text-tiny py-4 font-medium flex justify-between items-center cursor-pointer"
                onClick={() =>
                  setOpenMap({ ...openMap, [type]: !openMap[type] })
                }
              >
                <span className="font-bold">
                  {getTitle(
                    type === 'product_type' ? 'category' : type
                  ).toUpperCase()}
                </span>
                <div className="text-sm">
                  {openMap[type] ? (
                    <IoChevronUpSharp />
                  ) : (
                    <IoChevronDownSharp />
                  )}
                </div>
              </div>

              <Collapse in={openMap[type]}>
                {getCollapseValue(type)?.map((value, idx) => {
                  /** Render color tags */
                  if (type === 'color')
                    return (
                      <div
                        className="text-xs inline-flex items-center border p-1 mr-3 mb-3 cursor-pointer"
                        key={idx}
                        onClick={() =>
                          selectItem(type, { ...value, type } as any)
                        }
                      >
                        <span
                          className="w-3.5 h-3.5 inline-block mr-1"
                          style={getColorBackground(value.label).style}
                        />
                        <span>{value.label}</span>
                      </div>
                    )

                  if (type === 'product_type') {
                    return (
                      <ProductTypeItem
                        className="pl-3"
                        checkSelected={checkSelected}
                        value={value as any}
                        onSelect={selectItem}
                        onParentSelect={selectMultipleItem}
                        key={idx}
                      />
                    )
                  }
                  /** Ohter tags */
                  // TODO
                  const active = checkSelected(type, value.label)
                  return (
                    <FilterItem
                      value={value as any}
                      type={type}
                      active={Boolean(active)}
                      onSelect={selectItem}
                      key={idx}
                    />
                  )
                })}
              </Collapse>
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  )
}

export default FilterPanel
